import {
  flexRender,
  getCoreRowModel,
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
  useReactTable
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";

type PaginationConfig = {
  pageIndex: number;
  pageSize: number;
  total: number;
  onPageChange: (nextPageIndex: number) => void;
  onPageSizeChange?: (nextPageSize: number) => void;
  pageSizeOptions?: number[];
};

export type DataTableProps<TData> = {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  isLoading?: boolean;
  loadingMessage?: string;
  emptyMessage: string;
  renderToolbar?: ReactNode;
  pagination?: PaginationConfig;
  getRowId?: (row: TData, index: number) => string;
};

export function DataTable<TData>({
  columns,
  data,
  sorting,
  onSortingChange,
  isLoading = false,
  loadingMessage,
  emptyMessage,
  renderToolbar,
  pagination,
  getRowId
}: DataTableProps<TData>) {
  const { t } = useTranslation();
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getRowId
  });

  const hasRows = data.length > 0;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900">
      {renderToolbar ? (
        <div className="flex flex-col gap-3 border-b border-slate-100 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-900/60">
          {renderToolbar}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm text-slate-700 dark:text-slate-300">
          <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500 dark:bg-slate-800/70 dark:text-slate-400">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortState = header.column.getIsSorted();
                  const ariaSort =
                    sortState === "asc"
                      ? "ascending"
                      : sortState === "desc"
                        ? "descending"
                        : "none";
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className="px-6 py-3"
                      aria-sort={ariaSort}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 transition hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 dark:text-slate-400 dark:hover:text-slate-200"
                          onClick={header.column.getToggleSortingHandler()}
                          aria-label={t("common.table.sort_column", {
                            column: String(header.column.columnDef.header ?? header.id),
                            direction:
                              sortState === "asc"
                                ? t("common.table.sort_desc")
                                : t("common.table.sort_asc")
                          })}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <span
                            className="text-slate-300 transition group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-300"
                            aria-hidden
                          >
                            {sortState === "asc" ? "▲" : sortState === "desc" ? "▼" : "▵"}
                          </span>
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {isLoading ? (
              <tr>
                <td className="px-6 py-6 text-center text-sm text-slate-500 dark:text-slate-400" colSpan={columns.length}>
                  {loadingMessage ?? t("common.table.loading")}
                </td>
              </tr>
            ) : hasRows ? (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-6 py-4 align-middle">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-6 py-8 text-center text-sm font-medium text-slate-500 dark:text-slate-400" colSpan={columns.length}>
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pagination ? <TablePagination {...pagination} /> : null}
    </div>
  );
}

function TablePagination({
  pageIndex,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100]
}: PaginationConfig) {
  const { t } = useTranslation();
  const safePageSize = Math.max(1, pageSize);
  const totalPages = Math.max(1, Math.ceil(total / safePageSize));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);
  const start = total === 0 ? 0 : safePageIndex * safePageSize + 1;
  const end = total === 0 ? 0 : Math.min(total, start + safePageSize - 1);
  const canPrev = safePageIndex > 0;
  const canNext = safePageIndex + 1 < totalPages;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 bg-white px-4 py-3 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-3">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {t("common.table.rows_per_page")}
        </label>
        <select
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          value={pageSize}
          onChange={(event) => onPageSizeChange?.(Number.parseInt(event.target.value, 10))}
        >
          {pageSizeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {t("common.table.showing_range", { start, end, total })}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="pm-btn pm-btn-tonal pm-btn-sm"
          onClick={() => onPageChange(Math.max(0, safePageIndex - 1))}
          disabled={!canPrev}
        >
          {t("common.table.previous")}
        </button>
        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
          {safePageIndex + 1} / {totalPages}
        </span>
        <button
          type="button"
          className="pm-btn pm-btn-tonal pm-btn-sm"
          onClick={() => onPageChange(safePageIndex + 1)}
          disabled={!canNext}
        >
          {t("common.table.next")}
        </button>
      </div>
    </div>
  );
}
